import React, { useState, useEffect, useRef } from 'react';
import { 
  FileText, 
  Sparkles, 
  Copy, 
  Check, 
  Printer, 
  Download, 
  ShieldAlert, 
  Scale, 
  BookOpen, 
  Plus, 
  Trash2,
  CheckCircle2,
  Crown,
  Lock,
  Zap,
  Calendar,
  UserCheck,
  Maximize2,
  Minimize2,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';

const defaultCourts = [
  'IN THE HONORABLE SUPREME COURT OF PAKISTAN, ISLAMABAD',
  'IN THE HONORABLE HIGH COURT OF SINDH, AT KARACHI',
  'IN THE HONORABLE HIGH COURT OF SINDH, BENCH AT SUKKUR',
  'IN THE HONORABLE HIGH COURT OF SINDH, CIRCUIT COURT HYDERABAD',
  'IN THE HONORABLE LAHORE HIGH COURT, LAHORE',
  'IN THE HONORABLE LAHORE HIGH COURT, MULTAN BENCH',
  'IN THE HONORABLE LAHORE HIGH COURT, RAWALPINDI BENCH',
  'IN THE HONORABLE LAHORE HIGH COURT, BAHAWALPUR BENCH',
  'IN THE HONORABLE PESHAWAR HIGH COURT, PESHAWAR',
  'IN THE HONORABLE HIGH COURT OF BALOCHISTAN, QUETTA',
  'IN THE HONORABLE ISLAMABAD HIGH COURT, ISLAMABAD',
  'IN THE HONORABLE FEDERAL SHARIAT COURT OF PAKISTAN, ISLAMABAD',
  'BEFORE THE HONORABLE FEDERAL SERVICE TRIBUNAL, ISLAMABAD',
  'BEFORE THE HONORABLE PUNJAB SERVICE TRIBUNAL, LAHORE',
  'BEFORE THE HONORABLE SINDH SERVICE TRIBUNAL, KARACHI',
  'BEFORE THE HONORABLE KPK SERVICE TRIBUNAL, PESHAWAR',
  'BEFORE THE HONORABLE BALOCHISTAN SERVICE TRIBUNAL, QUETTA',
  'IN THE COURT OF WORTHY DISTRICT & SESSIONS JUDGE, LAHORE',
  'IN THE COURT OF WORTHY DISTRICT & SESSIONS JUDGE, KARACHI',
  'IN THE COURT OF WORTHY DISTRICT & SESSIONS JUDGE, QUETTA',
  'IN THE COURT OF SENIOR CIVIL JUDGE, LAHORE',
  'IN THE COURT OF SENIOR CIVIL JUDGE, KARACHI',
  'IN THE COURT OF SENIOR CIVIL JUDGE, QUETTA',
  'IN THE COURT OF PRESIDING OFFICER, BANKING COURT',
  'IN THE COURT OF PRESIDING OFFICER, ANTI-TERRORISM COURT (ATC)',
  'IN THE COURT OF PRESIDING OFFICER, LABOR COURT',
  'Others (Specify New Court Name)'
];

const expandedPleadingTemplates = [
  { id: 'writ_petition', name: '🏛️ Constitutional Writ Petition (Art. 199)', desc: 'High Court Writ Petition for Mandamus, Certiorari, Prohibition, or Quo-Warranto.' },
  { id: 'civil_plaint', name: '⚖️ Civil Suit Plaint (Order 7 Rule 1 CPC)', desc: 'Civil Suit for Declaration, Permanent Injunction, Specific Performance & Damages.' },
  { id: 'criminal_bail', name: '🛡️ Criminal Bail Application (u/s 497/498 CrPC)', desc: 'Pre-Arrest / Post-Arrest Bail Application before Sessions Court or High Court.' },
  { id: 'service_appeal', name: '📑 Service Appeal & Transfer Challenge', desc: 'Service Appeal challenging unauthorized transfer, posting, seniority, or deputation.' },
  { id: 'stay_application', name: '📜 Stay Application & Injunction (Order 39 R 1 & 2 CPC)', desc: 'Urgent Application for Temporary Injunction and Status Quo Order.' },
  { id: 'legal_notice', name: '💼 Formal Legal Notice / Breach Reply', desc: 'Pre-litigation Notice of Demand or Formal Legal Reply to Breach of Contract.' },
  { id: 'written_statement', name: '📂 Written Statement / Formal Reply (Order 8 Rule 1 CPC)', desc: 'Formal Defense Written Statement in response to Civil Suit or Application.' },
  { id: 'civil_revision', name: '📜 Memorandum of First Appeal / Civil Revision (s. 115 CPC)', desc: 'High Court Civil Revision Petition or First Appeal against Trial Court Order.' },
  { id: 'cpla_supreme_court', name: '🏛️ Supreme Court Petition for Leave to Appeal (CPLA Art. 185)', desc: 'Petition for Leave to Appeal before the Apex Supreme Court of Pakistan.' },
  { id: 'execution_application', name: '⚖️ Execution Application for Decree (Order 21 Rule 11 CPC)', desc: 'Application for Execution & Satisfaction of Decree passed by Court.' },
  { id: 'contempt_petition', name: '📑 Contempt of Court Petition (Art. 204 Constitution)', desc: 'Petition for Contempt of Court for willful disobedience of Court Order.' },
  { id: 'quashment_fir', name: '🛡️ Quashment of FIR Petition (Section 561-A CrPC)', desc: 'High Court Petition under inherent powers for Quashment of Illegal FIR.' },
  { id: 'custody_petition', name: '📜 Child Custody & Guardianship Petition (Act 1890)', desc: 'Guardian & Wards Petition for Custody of Minor and Visiting Rights.' },
  { id: 'arbitration_application', name: '💼 Arbitration Application (Section 20/33 Arbitration Act)', desc: 'Application to file Arbitration Agreement and appointment of Umpire.' },
  { id: 'others', name: '✨ Others (Specify New Pleading Template)', desc: 'Specify a custom new pleading format to add to dropdown.' }
];

const defaultCauseOfActionMap: Record<string, string> = {
  writ_petition: 'The cause of action accrued in favor of the Petitioner and against Respondents on 15-07-2026 when Respondent No. 2 passed the impugned arbitrary order, and continues every single day as fundamental rights under Art. 4, 9, 10-A & 25 remain infringed.',
  civil_plaint: 'The cause of action accrued on 15-07-2026 when Defendant No. 1 unlawfully refused to perform contractual obligations and threatened forcible dispossession from the suit property within the jurisdiction of this Court.',
  criminal_bail: 'The cause of action accrued on 10-07-2026 upon registration of false, frivolous & fabricated FIR No. 452/2026 u/s 302/34 PPC at P.S. Civil Lines due to ulterior motives and malice.',
  service_appeal: 'The cause of action accrued on 15-07-2026 when the impugned notification of transfer/posting was issued in violation of Civil Servants Act 1973 and statutory 3-year tenure policy.',
  stay_application: 'The cause of action accrued on 15-07-2026 when Defendants threatened immediate unlawful execution, eviction, and alteration of the subject premises.',
  legal_notice: 'The cause of action accrued on 15-07-2026 upon breach of contractual terms and failure to clear outstanding legal dues despite repeated oral demands.',
  written_statement: 'No valid cause of action ever accrued to the Plaintiff against the Answering Defendant; the suit is barred by limitation, estoppel, and Order 7 Rule 11 CPC.',
  civil_revision: 'The cause of action accrued on 15-07-2026 when the learned trial court passed the impugned order committing grave material irregularity and jurisdictional error.',
  cpla_supreme_court: 'The cause of action accrued on 15-07-2026 when the High Court passed the impugned judgment violating settled Apex Court precedents.',
  execution_application: 'The cause of action accrued on 15-07-2026 when Judgment Debtor failed to satisfy the decree passed in Case No. 102/2025.',
  contempt_petition: 'The cause of action accrued on 15-07-2026 when Respondents willfully disobey and defy order dated 01-06-2026 passed by this Honorable Court.',
  quashment_fir: 'The cause of action accrued on 15-07-2026 when police registered mala fide FIR No. 789/2026 without preliminary inquiry or incriminating evidence.',
  custody_petition: 'The cause of action accrued on 15-07-2026 when Respondent unlawfully removed the minor child from lawful custody and legal guardianship.',
  arbitration_application: 'The cause of action accrued on 15-07-2026 when commercial disputes arose and Respondent refused to concur in appointment of Arbitrator as per Clause 14.',
  others: 'The cause of action accrued in favor of the Petitioner on 15-07-2026 when the impugned illegal action was committed by Respondents, and continues every day as grievance remains unredressed.'
};

const defaultPrayerClauseMap: Record<string, string> = {
  writ_petition: `a) Issue a Writ of Certiorari declaring the impugned action / order / inaction of Respondents as illegal, arbitrary, mala fide, discriminatory, and void ab initio;
b) Issue a Writ of Mandamus directing Respondents to perform their statutory duties under law and grant full relief to Petitioner as per merit and law;
c) Grant interim stay / status quo restraining Respondents from taking adverse action or creating third-party rights during pendency hereof;
d) Grant costs of the petition.`,

  civil_plaint: `a) Decree for Declaration & Recovery of Physical Vacant Possession of suit property;
b) Mandatory Injunction directing grant of construction permission / NOC;
c) Permanent Injunction restraining interference or third-party allotment;
d) Award costs of the suit in favor of Plaintiff.`,

  criminal_bail: `a) Admit Applicant / Accused to Pre-Arrest / Post-Arrest Bail subject to furnishing surety bonds to satisfaction of Court.`,

  service_appeal: `a) Set aside impugned transfer / posting notification and restore Petitioner to position with full consequential back benefits.`,

  stay_application: `a) Grant interim order of Temporary Injunction and Status Quo restraining Defendants from interfering with peaceful possession.`,

  legal_notice: `a) Satisfy lawful demand within 14 days, failing which civil and criminal legal proceedings shall be instituted.`,

  written_statement: `a) Dismiss suit of Plaintiff with heavy special costs under Section 35-A CPC.`,

  civil_revision: `a) Set aside impugned order passed by trial court and allow civil revision petition.`,

  cpla_supreme_court: `a) Grant Leave to Appeal against impugned judgment and suspend operation thereof.`,

  execution_application: `a) Execute Decree by attachment and sale of Judgment Debtor's property.`,

  contempt_petition: `a) Summon and punish Respondents under Article 204 of Constitution for willful contempt of Court Order.`,

  quashment_fir: `a) Quash FIR and all consequential proceedings pending before trial court.`,

  custody_petition: `a) Deliver permanent custody of minor child to Petitioner and grant interim visiting rights.`,

  arbitration_application: `a) File Arbitration Agreement in Court and appoint independent Arbitrator.`,

  others: `a) Grant principal relief prayed for along with costs of proceedings.`
};

const defaultStatutoryMap: Record<string, string> = {
  writ_petition: 'Article 199, 4, 9, 10-A, 18, 23 & 24 of Constitution of Pakistan 1973',
  civil_plaint: 'Order 7 Rule 1 & Section 9, 15, 20 of Code of Civil Procedure 1908; Specific Relief Act 1877',
  criminal_bail: 'Section 497 & 498 of Code of Criminal Procedure 1898; PPC 1860',
  service_appeal: 'Section 4 of Service Tribunals Act 1973 & Civil Servants Act 1973',
  stay_application: 'Order 39 Rule 1 & 2 read with Section 151 of Code of Civil Procedure 1908',
  legal_notice: 'Section 73 of Contract Act 1872 & Relevant Statutory Laws of Pakistan',
  written_statement: 'Order 8 Rule 1 & Section 35-A of Code of Civil Procedure 1908',
  civil_revision: 'Section 115 read with Order 41 of Code of Civil Procedure 1908',
  cpla_supreme_court: 'Article 185(3) of Constitution of Pakistan 1973 & Supreme Court Rules 1980',
  execution_application: 'Order 21 Rule 11 & Section 51 of Code of Civil Procedure 1908',
  contempt_petition: 'Article 204 of Constitution of Pakistan read with Contempt of Court Act 2003',
  quashment_fir: 'Section 561-A of Code of Criminal Procedure 1898',
  custody_petition: 'Section 7 & 25 of Guardians and Wards Act 1890',
  arbitration_application: 'Section 20 & 33 of Arbitration Act 1940',
  others: 'Relevant Statutory Provisions & High Court Rules of Pakistan'
};

interface SmartLegalDraftingProps {
  isModalView?: boolean;
  onClose?: () => void;
}

export const SmartLegalDrafting: React.FC<SmartLegalDraftingProps> = ({ isModalView = false, onClose }) => {
  const { user } = useAuth();
  const { colors, themeMode, setThemeMode } = useTheme();

  // WINDOW WITHIN WINDOW CONTROLS & DYNAMIC RESIZING (Horizontal & Vertical Drag)
  const [windowState, setWindowState] = useState<'normal' | 'fullscreen' | 'minimized'>('normal');
  const [windowSize, setWindowSize] = useState<{ width: number; height: number }>({ width: 1100, height: 720 });
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<{ startX: number; startY: number; startW: number; startH: number } | null>(null);

  // LISTEN FOR GLOBAL 'openSmartDrafting' EVENT FROM NAV BAR BADGES TO RESTORE FROM MINIMIZED
  useEffect(() => {
    const handleRestore = () => {
      setWindowState('normal');
    };
    window.addEventListener('openSmartDrafting', handleRestore);
    return () => window.removeEventListener('openSmartDrafting', handleRestore);
  }, []);

  // MOUSE DRAG EVENT HANDLERS FOR DYNAMIC WINDOW RESIZING
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !resizeRef.current) return;
      const dx = e.clientX - resizeRef.current.startX;
      const dy = e.clientY - resizeRef.current.startY;
      const newW = Math.max(450, Math.min(window.innerWidth - 30, resizeRef.current.startW + dx));
      const newH = Math.max(400, Math.min(window.innerHeight - 30, resizeRef.current.startH + dy));
      setWindowSize({ width: newW, height: newH });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleStartResize = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startW: windowSize.width,
      startH: windowSize.height
    };
  };

  // Role Enforcement: Restricted to Advocates, Practitioners, and Law Firms
  const userRole = (user?.role || 'client').toLowerCase();
  const allowedRoles = ['practitioner', 'law_firm', 'associate', 'superuser', 'db_admin', 'advocate', 'administrator'];
  const isAuthorized = allowedRoles.includes(userRole);
  const isDBA = ['superuser', 'db_admin', 'administrator'].includes(userRole);

  // DBA MASTER OVERRIDE & SUBSCRIPTION STATE
  const [globalFreeAccess, setGlobalFreeAccess] = useState<boolean>(true);
  const [userSubscriptionActive, setUserSubscriptionActive] = useState<boolean>(true);
  const [freeTrialDays, setFreeTrialDays] = useState<number>(30);

  const [courtOptions, setCourtOptions] = useState<string[]>(defaultCourts);
  const [selectedCourt, setSelectedCourt] = useState<string>(defaultCourts[1]);
  const [customCourtInput, setCustomCourtInput] = useState<string>('');
  const [courtSearchQuery, setCourtSearchQuery] = useState<string>('');
  const [isCourtDropdownOpen, setIsCourtDropdownOpen] = useState<boolean>(false);

  const [pleadingOptions, setPleadingOptions] = useState<any[]>(expandedPleadingTemplates);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('writ_petition');
  const [customTemplateInput, setCustomTemplateInput] = useState<string>('');
  const [templateSearchQuery, setTemplateSearchQuery] = useState<string>('');
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState<boolean>(false);

  const [petitioners, setPetitioners] = useState<string[]>(['Muhammad Ali / Muhammad Aslam']);
  const [respondents, setRespondents] = useState<string[]>(['Chief Secretary, Government of Balochistan / Concerned Department & Others']);

  const [causeOfAction, setCauseOfAction] = useState<string>(defaultCauseOfActionMap['writ_petition']);
  const [keyFacts, setKeyFacts] = useState<string>('');
  const [statutorySections, setStatutorySections] = useState<string>(defaultStatutoryMap['writ_petition']);
  const [reliefRequested, setReliefRequested] = useState<string>(defaultPrayerClauseMap['writ_petition']);

  const [drafting, setDrafting] = useState<boolean>(false);
  const [generatedDraft, setGeneratedDraft] = useState<string>('');
  const [copied, setCopied] = useState<boolean>(false);
  const [copiedHints, setCopiedHints] = useState<boolean>(false);
  const [isAdvancedOptionsOpen, setIsAdvancedOptionsOpen] = useState<boolean>(false);

  // AUTO-POPULATE Cause of Action, Statutory Sections, and Prayer Clause on Template Change
  useEffect(() => {
    const key = defaultCauseOfActionMap[selectedTemplate] ? selectedTemplate : 'others';
    setCauseOfAction(defaultCauseOfActionMap[key] || defaultCauseOfActionMap['others']);
    setStatutorySections(defaultStatutoryMap[key] || defaultStatutoryMap['others']);
    setReliefRequested(defaultPrayerClauseMap[key] || defaultPrayerClauseMap['others']);
  }, [selectedTemplate]);



  // IF MINIMIZED -> DOCK AS FLOATING BADGE AT BOTTOM-RIGHT CORNER
  if (windowState === 'minimized') {
    return (
      <div 
        onClick={() => setWindowState('normal')}
        title="Click to restore Smart Legal Drafting & Pleading"
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '770px',
          zIndex: 99999,
          backgroundColor: '#d97706',
          color: '#0f172a',
          padding: '0.85rem 1.35rem',
          borderRadius: '50px',
          cursor: 'pointer',
          boxShadow: '0 10px 25px rgba(217, 119, 6, 0.5), 0 4px 10px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.65rem',
          fontWeight: '900',
          fontSize: '0.95rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <Scale size={20} />
        <span>✍️ Smart Legal Drafting</span>
      </div>
    );
  }

  // Handlers for Multiple Petitioners
  const handleAddPetitioner = () => setPetitioners(prev => [...prev, '']);
  const handlePetitionerChange = (index: number, val: string) => {
    setPetitioners(prev => {
      const updated = [...prev];
      updated[index] = val;
      return updated;
    });
  };
  const handleRemovePetitioner = (index: number) => {
    if (petitioners.length === 1) return;
    setPetitioners(prev => prev.filter((_, i) => i !== index));
  };

  // Handlers for Multiple Respondents
  const handleAddRespondent = () => setRespondents(prev => [...prev, '']);
  const handleRespondentChange = (index: number, val: string) => {
    setRespondents(prev => {
      const updated = [...prev];
      updated[index] = val;
      return updated;
    });
  };
  const handleRemoveRespondent = (index: number) => {
    if (respondents.length === 1) return;
    setRespondents(prev => prev.filter((_, i) => i !== index));
  };

  // Helper to render Markdown Bold, Underlined, Centered & Table elements cleanly in UI Preview Box
  const renderFormattedDraft = (text: string) => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let tableBuffer: string[] = [];

    const flushTableBuffer = (keyPrefix: string) => {
      if (tableBuffer.length < 2) {
        tableBuffer.forEach((tblLine, idx) => {
          elements.push(<div key={`${keyPrefix}_raw_${idx}`}>{tblLine}</div>);
        });
        tableBuffer = [];
        return;
      }

      // Filter out markdown alignment separator line (|:---:|:---|)
      const dataRows = tableBuffer.filter(r => !/^\|[\s:\-]*\|/.test(r.trim()));
      tableBuffer = [];

      elements.push(
        <div key={`${keyPrefix}_tbl_wrap`} style={{ margin: '16px 0', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #475569', fontSize: '13px', fontFamily: 'monospace' }}>
            <tbody>
              {dataRows.map((rowStr, rIdx) => {
                const cells = rowStr.split('|').slice(1, -1).map(c => c.trim());
                const isHeader = rIdx === 0;
                return (
                  <tr key={rIdx} style={{ backgroundColor: isHeader ? '#f8fafc' : 'transparent' }}>
                    {cells.map((cellText, cIdx) => {
                      const CellTag = isHeader ? 'th' : 'td';
                      return (
                        <CellTag 
                          key={cIdx} 
                          style={{ 
                            border: '1px solid #475569', 
                            padding: '8px 12px', 
                            fontWeight: isHeader ? 'bold' : 'normal',
                            textAlign: cIdx === 0 || cIdx === 2 ? 'center' : 'left'
                          }}
                        >
                          {cellText}
                        </CellTag>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    };

    lines.forEach((line, lIdx) => {
      const trimmed = line.trim();

      // Detect Table Line
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        tableBuffer.push(trimmed);
        return;
      } else if (tableBuffer.length > 0) {
        flushTableBuffer(`tbl_${lIdx}`);
      }

      // Ignore Fee Box legacy markers if any
      if (trimmed.includes('SPACE FOR AFFIXING COURT FEE') || trimmed.includes('height: 1.5in') || /^[\=\-\+\|]{10,}$/.test(trimmed)) {
        return;
      }

      // Page Break Divider (Affidavit or Pleading Hints on new page)
      if (trimmed === '---' || trimmed.startsWith('--- SEPARATE PAGE')) {
        const nextFewLines = lines.slice(lIdx + 1, lIdx + 6).join(' ');
        const isHintsPage = /PLEADING HINTS FOR PRACTITIONERS/i.test(nextFewLines);

        if (isHintsPage) {
          elements.push(
            <div id="pleading-hints-anchor" key={`pb_hints_${lIdx}`} style={{ pageBreakBefore: 'always', margin: '40px 0 24px 0', borderTop: '3px solid #d97706', paddingTop: '18px', textAlign: 'center' }}>
              <span style={{ backgroundColor: '#fffbeb', color: '#b45309', border: '1px solid #fde68a', borderRadius: '30px', padding: '0.5rem 1.35rem', fontWeight: '900', fontSize: '13.5px', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 2px 8px rgba(217, 119, 6, 0.18)' }}>
                ⚖️ --- SEPARATE PAGE: PLEADING HINTS FOR PRACTITIONERS (BENCH AID & ORAL ARGUMENTS) ---
              </span>
            </div>
          );
        } else {
          elements.push(
            <div key={`pb_aff_${lIdx}`} style={{ pageBreakBefore: 'always', margin: '32px 0', borderTop: '2px dashed #3b82f6', paddingTop: '16px', textAlign: 'center', color: '#2563eb', fontWeight: 'bold', fontSize: '13px' }}>
              📄 --- SEPARATE PAGE: AFFIDAVIT IN SUPPORT (1 PAGE) ---
            </div>
          );
        }
        return;
      }

      // Strip asterisks (*), u tags, center tags, page tags, and clean (S) tags from line
      let cleanLine = line
        .replace(/\*/g, '')
        .replace(/\(S\)/g, '')
        .replace(/\[/g, '')
        .replace(/\]/g, '')
        .replace(/<\/?u>/gi, '')
        .replace(/<\/?center>/gi, '')
        .replace(/<div align="center">/gi, '')
        .replace(/<\/div>/gi, '')
        .replace(/\(Page-\d+\/\d+\)/gi, '');
      const trimmedClean = cleanLine.trim();

      if (!trimmedClean) {
        elements.push(<div key={`line_${lIdx}`} style={{ minHeight: '1.2em' }}></div>);
        return;
      }

      const isCourtTitle = /^(IN THE HONORABLE|BEFORE THE HONORABLE|IN THE COURT OF)/i.test(trimmedClean);
      const isSubjectLine = /^(MEMORANDUM OF|SUBJECT:)/i.test(trimmedClean);
      const isHintsHeader = /^PLEADING HINTS FOR PRACTITIONERS/i.test(trimmedClean);
      const isHintSection = /^[1-6]\.\s+[A-Z\s&()/-]+/i.test(trimmedClean);
      const isHintBullet = trimmedClean.startsWith('•');
      const isPageHeader = /^(AFFIDAVIT IN SUPPORT|REGISTERED A\.D\.|FORMAL LEGAL NOTICE)/i.test(trimmedClean);

      if (isCourtTitle) {
        elements.push(
          <div key={`line_${lIdx}`} style={{ textAlign: 'center', fontWeight: 'bold', textDecoration: 'none', margin: '14px 0', fontSize: '15px' }}>
            {trimmedClean}
          </div>
        );
      } else if (isSubjectLine) {
        elements.push(
          <div key={`line_${lIdx}`} style={{ textAlign: 'justify', fontWeight: 'bold', textDecoration: 'underline', lineHeight: '1.5', margin: '16px 0', fontSize: '14px' }}>
            {trimmedClean}
          </div>
        );
      } else if (isHintsHeader) {
        elements.push(
          <div key={`hints_h_${lIdx}`} style={{ textAlign: 'center', fontWeight: 'bold', textDecoration: 'none', color: '#b45309', margin: '20px 0 14px 0', fontSize: '16px', letterSpacing: '0.5px' }}>
            {trimmedClean}
          </div>
        );
      } else if (isHintSection) {
        elements.push(
          <div key={`hint_sec_${lIdx}`} style={{ textAlign: 'justify', fontWeight: 'normal', textDecoration: 'none', color: '#0f172a', backgroundColor: '#fffbeb', borderLeft: '4px solid #d97706', padding: '0.5rem 0.85rem', margin: '18px 0 8px 0', borderRadius: '0 8px 8px 0', fontSize: '14px', lineHeight: '1.5' }}>
            {trimmedClean}
          </div>
        );
      } else if (isHintBullet) {
        elements.push(
          <div key={`hint_b_${lIdx}`} style={{ textAlign: 'justify', fontWeight: 'normal', textDecoration: 'none', paddingLeft: '1.25rem', textIndent: '-1rem', margin: '6px 0', lineHeight: '1.5', color: '#1e293b', fontSize: '13.5px' }}>
            {trimmedClean}
          </div>
        );
      } else if (isPageHeader) {
        elements.push(
          <div key={`line_${lIdx}`} style={{ textAlign: 'center', fontWeight: 'bold', textDecoration: 'none', margin: '14px 0', fontSize: '14px' }}>
            {trimmedClean}
          </div>
        );
      } else {
        elements.push(
          <div key={`line_${lIdx}`} style={{ textAlign: 'justify', fontWeight: 'normal', textDecoration: 'none', lineHeight: '1.5', minHeight: '1.2em' }}>
            {trimmedClean}
          </div>
        );
      }
    });

    if (tableBuffer.length > 0) {
      flushTableBuffer('tbl_end');
    }

    return elements;
  };

  // Add Custom Court Name to Dropdown List
  const handleAddCustomCourt = () => {
    if (!customCourtInput.trim()) return;
    const formattedNewCourt = customCourtInput.trim().toUpperCase();
    if (!courtOptions.includes(formattedNewCourt)) {
      setCourtOptions([formattedNewCourt, ...courtOptions]);
      setSelectedCourt(formattedNewCourt);
      setCustomCourtInput('');
    } else {
      setSelectedCourt(formattedNewCourt);
    }
  };

  // Add Custom Pleading Template to Dropdown List
  const handleAddCustomTemplate = () => {
    if (!customTemplateInput.trim()) return;
    const formattedName = customTemplateInput.trim();
    const newId = `custom_${Date.now()}`;
    const newTemplateItem = {
      id: newId,
      name: `📋 ${formattedName}`,
      desc: `Custom user-defined pleading template format for ${formattedName}.`
    };

    setPleadingOptions(prev => [newTemplateItem, ...prev]);
    setSelectedTemplate(newId);
    setCustomTemplateInput('');
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check Subscription / Exemption Status
    if (!globalFreeAccess && !userSubscriptionActive && !isDBA) {
      alert('🔒 SUBSCRIPTION REQUIRED: Please active your advocate subscription or request Superuser/DBA exemption to generate legal pleading drafts.');
      return;
    }

    const activeCourtName = selectedCourt === 'Others (Specify New Court Name)' 
      ? (customCourtInput.trim() || 'IN THE HONORABLE HIGH COURT') 
      : selectedCourt;

    if (!keyFacts.trim()) {
      alert('Please provide the brief facts of your case. AI will automatically deduce, refine and elaborate all legal details.');
      return;
    }

    const validPetitioners = petitioners.filter(p => p.trim());
    const finalPetitioners = validPetitioners.length > 0 ? validPetitioners : ['Muhammad Aslam Advocate & Others'];

    const validRespondents = respondents.filter(r => r.trim());
    const finalRespondents = validRespondents.length > 0 ? validRespondents : ['Government of Pakistan through Concerned Department & Others'];

    setDrafting(true);
    setGeneratedDraft(''); // Immediately erase & clear previous draft output
    try {
      const res = await api.post(`/ai/draft?_t=${Date.now()}`, {
        templateType: selectedTemplate,
        courtName: activeCourtName,
        petitioners: finalPetitioners,
        respondents: finalRespondents,
        causeOfAction: causeOfAction.trim(),
        keyFacts: keyFacts.trim(),
        statutorySections: statutorySections.trim(),
        reliefRequested: reliefRequested.trim()
      }, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (res.data && res.data.draftText) {
        setGeneratedDraft(res.data.draftText);
      }
    } catch (err: any) {
      console.error('Draft generation error:', err);
      const errMsg = err.response?.data?.message || err.message || 'Failed to generate legal draft.';
      alert(errMsg);
    } finally {
      setDrafting(false);
    }
  };

  const handleScrollToHints = () => {
    const el = document.getElementById('pleading-hints-anchor');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCopyHints = () => {
    if (!generatedDraft) return;
    const hintsIdx = generatedDraft.indexOf('PLEADING HINTS FOR PRACTITIONERS');
    if (hintsIdx !== -1) {
      const hintsOnly = generatedDraft.slice(hintsIdx).trim();
      navigator.clipboard.writeText(hintsOnly);
      setCopiedHints(true);
      setTimeout(() => setCopiedHints(false), 2000);
    } else {
      handleCopy();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleResetForm = () => {
    setPetitioners(['']);
    setRespondents(['']);
    setCauseOfAction('');
    setKeyFacts('');
    setStatutorySections('');
    setReliefRequested('');
    setGeneratedDraft('');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedDraft);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const cleanDraft = generatedDraft
        .replace(/\*/g, '')
        .replace(/\(S\)/g, '');

      const lines = cleanDraft.split('\n');
      let formattedHtml = '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) {
          formattedHtml += '<div style="min-height: 1.2em;"></div>';
          continue;
        }

        if (trimmed === '---' || trimmed.startsWith('--- SEPARATE PAGE')) {
          formattedHtml += '<div style="page-break-before: always; margin-top: 24px;"></div>';
          continue;
        }

        const isCourtTitle = /^(IN THE HONORABLE|BEFORE THE HONORABLE|IN THE COURT OF)/i.test(trimmed);
        const isSubjectLine = /^(MEMORANDUM OF|SUBJECT:)/i.test(trimmed);
        const isPageHeader = /^(AFFIDAVIT IN SUPPORT|PLEADING HINTS FOR PRACTITIONERS|REGISTERED A\.D\.|FORMAL LEGAL NOTICE)/i.test(trimmed);

        if (isCourtTitle) {
          formattedHtml += `<div style="text-align: center; font-weight: bold; text-decoration: none; margin: 14px 0;">${trimmed}</div>`;
        } else if (isSubjectLine) {
          formattedHtml += `<div style="text-align: justify; font-weight: bold; text-decoration: underline; line-height: 1.5; margin: 16px 0;">${trimmed}</div>`;
        } else if (isPageHeader) {
          formattedHtml += `<div style="text-align: center; font-weight: bold; text-decoration: none; margin: 16px 0;">${trimmed}</div>`;
        } else {
          formattedHtml += `<div style="text-align: justify; font-weight: normal; text-decoration: none; line-height: 1.5; margin-bottom: 6px;">${trimmed}</div>`;
        }
      }

      printWindow.document.write(`
        <html>
          <head>
            <title>Formal Legal Pleading Draft - ELum Law</title>
            <style>
              @page {
                size: legal; /* Legal Paper Size 8.5in x 14in */
                margin-top: 0.75in;
                margin-bottom: 1.0in;
                margin-left: 1.25in;
                margin-right: 0.75in;
              }
              body { 
                font-family: 'Courier New', Courier, monospace; 
                font-size: 12pt; 
                line-height: 1.5; 
                text-align: justify; 
                color: #000;
                margin: 0;
                padding: 0;
              }
              div {
                font-family: 'Courier New', Courier, monospace;
                font-size: 12pt;
              }
            </style>
          </head>
          <body>
            <div>${formattedHtml}</div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 500);
    }
  };

  // EXPLICIT MS WORD (.DOC) DOWNLOAD ONLY (Legal Paper Size 8.5"x14", Courier New 12pt, Margins: Top 0.75", Bottom 1.0", Left 1.25", Right 0.75")
  // EXPLICIT MS WORD NATIVE (.DOCX) DOWNLOAD (Legal Paper Size 8.5"x14", Courier New 12pt, Margins: Top 0.75", Bottom 1.0", Left 1.0", Right 1.0" -> Text Width 16.51 cm)
  const handleDownloadDoc = async () => {
    if (!globalFreeAccess && !userSubscriptionActive && !isDBA) {
      alert('🔒 SUBSCRIPTION REQUIRED: MS Word download requires an active advocate subscription or Superuser/DBA exemption.');
      return;
    }

    if (!generatedDraft) {
      alert('No legal draft available to download.');
      return;
    }

    try {
      const response = await api.post(
        '/ai/download-docx',
        {
          draftText: generatedDraft,
          fileName: `${selectedTemplate}_legal_size_pleading`
        },
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedTemplate}_legal_size_pleading.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return;
    } catch (docxErr) {
      console.warn('Native DOCX download endpoint error, executing HTML fallback:', docxErr);
    }

    // Fallback: 16.5 cm Fixed Table HTML Format
    let cleanDraft = generatedDraft
      .replace(/\*/g, '')
      .replace(/\(S\)/g, '')
      .replace(/\[/g, '')
      .replace(/\]/g, '')
      .replace(/\(Page-\d+\/\d+\)/gi, '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/&lt;\/?u&gt;/gi, '')
      .replace(/&lt;center&gt;/gi, '')
      .replace(/&lt;\/center&gt;/gi, '')
      .replace(/&lt;div style="height: 1.5in.*?&lt;\/div&gt;/gi, '');

    const formattedParagraphs = cleanDraft.split('\n').map(line => {
      const trimmed = line.trim();
      if (!trimmed) {
        return '<p class="MsoNormal" style="margin:0in; margin-bottom:4pt; font-size:12.0pt; font-family:\'Courier New\',Courier,monospace;">&nbsp;</p>';
      }
      
      const isCentered = /^(IN THE|BEFORE THE|AFFIDAVIT IN SUPPORT|MEMORANDUM OF|REGISTERED A\.D\.|FORMAL LEGAL NOTICE)/i.test(trimmed);
      const isSectionHeader = /^(I\.|II\.|III\.|IV\.|SECTION I|SECTION II|SECTION III|SECTION IV|DEMAND FOR COMPLIANCE|NOTICE OF LITIGATION|SUBJECT:)/i.test(trimmed);

      if (isCentered) {
        return `<p class="MsoNormal" align="center" style="text-align:center; margin:12pt 0; font-size:12.0pt; font-family:'Courier New',Courier,monospace; font-weight:bold;"><b><u>${trimmed}</u></b></p>`;
      }
      if (isSectionHeader) {
        return `<p class="MsoNormal" style="margin:14pt 0 6pt 0; font-size:12.0pt; font-family:'Courier New',Courier,monospace; font-weight:bold;"><b><u>${trimmed}</u></b></p>`;
      }
      if (trimmed === '---') {
        return '<br clear="all" style="page-break-before:always; mso-break-type:page-break" />';
      }

      return `<p class="MsoNormal" style="text-align:justify; margin:0in; margin-bottom:6pt; font-size:12.0pt; font-family:'Courier New',Courier,monospace; line-height:150%; text-justify:inter-ideograph;">${trimmed}</p>`;
    }).join('\n');

    const htmlHeader = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' 
            xmlns:w='urn:schemas-microsoft-com:office:word' 
            xmlns:m='http://schemas.microsoft.com/office/2004/12/omml'
            xmlns='http://www.w3.org/TR/REC-html40'>
      <head>
        <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
        <meta name="ProgId" content="Word.Document">
        <meta name="Generator" content="Microsoft Word 15">
        <meta name="Originator" content="Microsoft Word 15">
        <title>Legal Pleading Draft - Legal Size</title>
        <!--[if gte mso 9]>
        <xml>
          <w:WordDocument>
            <w:View>Print</w:View>
            <w:Zoom>100</w:Zoom>
            <w:DoNotOptimizeForBrowser/>
          </w:WordDocument>
        </xml>
        <![endif]-->
        <style>
          @page {
            size: 612.0pt 1008.0pt;
            margin: 72.0pt 72.0pt 72.0pt 90.0pt;
            mso-header-margin: 36.0pt;
            mso-footer-margin: 36.0pt;
          }
          @page Section1 {
            size: 612.0pt 1008.0pt;
            margin: 72.0pt 72.0pt 72.0pt 90.0pt;
            mso-header-margin: 36.0pt;
            mso-footer-margin: 36.0pt;
            mso-page-orientation: portrait;
          }
          div.Section1 { page: Section1; }
          body { font-family: "Courier New", Courier, monospace; font-size: 12.0pt; line-height: 150%; margin: 0in; padding: 0in; }
          p.MsoNormal, p { mso-style-name: "Normal"; font-family: "Courier New", Courier, monospace !important; font-size: 12.0pt !important; line-height: 150% !important; text-align: justify !important; margin: 0in 0in 6pt 0in !important; }
        </style>
      </head>
      <body>
        <div class="Section1">
          <table role="presentation" border="0" cellpadding="0" cellspacing="0" align="center" style="width: 15.87cm !important; max-width: 15.87cm !important; width: 600px !important; margin: 0 auto !important; table-layout: fixed !important;">
            <tr>
              <td style="width: 15.87cm !important; max-width: 15.87cm !important; width: 600px !important; font-family: 'Courier New', Courier, monospace; font-size: 12.0pt; line-height: 150%; text-align: justify;">
                ${formattedParagraphs}
              </td>
            </tr>
          </table>
        </div>
      </body>
      </html>
    `;

    const blob = new Blob(['\ufeff' + htmlHeader], {
      type: 'application/msword'
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedTemplate}_legal_size_pleading.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // RESTRICTED ACCESS NOTICE FOR CLIENTS / UNAPPROVED ROLES
  if (!isAuthorized) {
    return (
      <div style={{ maxWidth: '900px', margin: '4rem auto', padding: '2.5rem', backgroundColor: 'white', borderRadius: '16px', border: '2px solid #fecdd3', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', textAlign: 'center', fontFamily: "'Inter', sans-serif" }}>
        <ShieldAlert size={56} color="#dc2626" style={{ marginBottom: '1rem' }} />
        <h2 style={{ fontSize: '1.6rem', fontWeight: '800', color: '#9f1239', margin: '0 0 0.5rem 0' }}>
          🔒 RESTRICTED ADVOCATES & LAW FIRMS SUITE
        </h2>
        <p style={{ fontSize: '1rem', color: '#475569', maxWidth: '650px', margin: '0 auto 1.5rem auto', lineHeight: '1.6' }}>
          The <strong>Smart Legal Drafting & Pleading Engine</strong> is strictly reserved for verified <strong>Practitioners, Advocates, and Law Firms</strong> under Supreme Court & High Court Practice Rules.
        </p>

        <div style={{ backgroundColor: '#fff1f2', padding: '1.25rem', borderRadius: '12px', border: '1px solid #ffe4e6', textAlign: 'left', marginBottom: '1.75rem', fontSize: '0.9rem', color: '#881337' }}>
          <h4 style={{ margin: '0 0 0.5rem 0', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Scale size={18} color="#dc2626" /> Registered Roles Authorized for Legal Drafting:
          </h4>
          <ul style={{ margin: 0, paddingLeft: '1.25rem', lineHeight: '1.6' }}>
            <li>Senior Advocates of Supreme Court & High Court Bar Members</li>
            <li>Registered Law Firms & Partner Advocates</li>
            <li>Associate Legal Practitioners & Government Law Officers</li>
            <li>Database Administrators & System Superusers</li>
          </ul>
        </div>
      </div>
    );
  }

  const content = (
    <div style={{ maxWidth: isModalView ? '100%' : '1400px', margin: '0 auto', padding: '1.5rem', fontFamily: "'Inter', sans-serif", position: 'relative', zIndex: 10, backgroundColor: colors.pageBg, color: colors.textColor, minHeight: isModalView ? '100%' : '100vh', transition: 'all 0.3s ease', display: 'flex', flexDirection: 'column', flex: 1, overflowY: 'auto' }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem', borderBottom: `2px solid ${colors.goldPrimary}`, paddingBottom: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: '800', color: colors.titleColor, margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <FileText size={28} color={colors.goldPrimary} /> Smart Legal Drafting & Pleading Engine
          </h1>
          <span style={{ fontSize: '0.85rem', color: colors.textMutedColor, marginTop: '0.3rem', display: 'block', fontWeight: '500' }}>
            Standard Legal Paper (8.5" × 14") • 1.5-inch Margin • Court Format Compliant
          </span>
        </div>

        {/* 3-WAY THEME CONTROLLER & ACCESS BADGES */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginLeft: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.15)', padding: '0.2rem', borderRadius: '8px', border: `1px solid ${colors.borderThemeColor}` }}>
            <button 
              onClick={() => setThemeMode('dark')}
              style={{ backgroundColor: themeMode === 'dark' ? colors.goldPrimary : 'transparent', color: themeMode === 'dark' ? '#0f172a' : colors.textColor, border: 'none', borderRadius: '6px', padding: '0.35rem 0.65rem', fontSize: '0.78rem', fontWeight: '800', cursor: 'pointer' }}
            >
              🌙 Dark
            </button>
            <button 
              onClick={() => setThemeMode('light')}
              style={{ backgroundColor: themeMode === 'light' ? colors.goldPrimary : 'transparent', color: themeMode === 'light' ? '#0f172a' : colors.textColor, border: 'none', borderRadius: '6px', padding: '0.35rem 0.65rem', fontSize: '0.78rem', fontWeight: '800', cursor: 'pointer' }}
            >
              ☀️ Light
            </button>
            <button 
              onClick={() => setThemeMode('easy')}
              style={{ backgroundColor: themeMode === 'easy' ? '#10b981' : 'transparent', color: themeMode === 'easy' ? '#ffffff' : colors.textColor, border: 'none', borderRadius: '6px', padding: '0.35rem 0.65rem', fontSize: '0.78rem', fontWeight: '800', cursor: 'pointer' }}
            >
              👁️ Eye-Comfort
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', padding: '0.45rem 0.85rem', borderRadius: '20px', color: '#15803d', fontSize: '0.82rem', fontWeight: '800' }}>
            <CheckCircle2 size={16} /> Verified Advocate Access
          </div>

          {/* WINDOW ACTION CONTROLS (_ [ ] X) AT EXTREME TOP RIGHT IF MODAL VIEW */}
          {isModalView && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '0.5rem' }}>
              <button 
                onClick={() => setWindowState('minimized')} 
                title="Minimize Window" 
                style={{ backgroundColor: 'rgba(0,0,0,0.15)', color: colors.textColor, border: 'none', borderRadius: '6px', padding: '0.3rem 0.6rem', cursor: 'pointer', fontWeight: 'bold' }}
              >
                _
              </button>
              <button 
                onClick={() => setWindowState(windowState === 'fullscreen' ? 'normal' : 'fullscreen')} 
                title={windowState === 'fullscreen' ? "Restore Size" : "Maximize Fullscreen"} 
                style={{ backgroundColor: 'rgba(0,0,0,0.15)', color: colors.textColor, border: 'none', borderRadius: '6px', padding: '0.3rem 0.6rem', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                {windowState === 'fullscreen' ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
              {onClose && (
                <button 
                  onClick={onClose} 
                  title="Close Window" 
                  style={{ backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', padding: '0.3rem 0.6rem', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* DEDICATED ENGINE MODE SWITCHER: COURT PLEADINGS vs OUT-OF-COURT LEGAL NOTICE */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        flexWrap: 'wrap', 
        gap: '1rem', 
        backgroundColor: '#f8fafc', 
        border: '1px solid #cbd5e1', 
        borderRadius: '14px', 
        padding: '0.85rem 1.25rem', 
        marginBottom: '1.5rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: '800', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            ENGINE DRAFTING MODE:
          </span>

          {/* MODE BUTTON 1: COURT PLEADINGS ENGINE */}
          <button
            type="button"
            onClick={() => setSelectedTemplate('writ_petition')}
            style={{
              padding: '0.55rem 1.1rem',
              borderRadius: '8px',
              border: selectedTemplate !== 'legal_notice' ? '2px solid #0284c7' : '1px solid #cbd5e1',
              backgroundColor: selectedTemplate !== 'legal_notice' ? '#e0f2fe' : '#ffffff',
              color: selectedTemplate !== 'legal_notice' ? '#0369a1' : '#64748b',
              fontWeight: '800',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: selectedTemplate !== 'legal_notice' ? '0 2px 6px rgba(2,132,199,0.15)' : 'none',
              transition: 'all 0.2s ease'
            }}
          >
            🏛️ Court Pleading Engine
          </button>

          {/* MODE BUTTON 2: FORMAL LEGAL NOTICE ENGINE WITH DISTINCT BADGE */}
          <button
            type="button"
            onClick={() => setSelectedTemplate('legal_notice')}
            style={{
              padding: '0.55rem 1.1rem',
              borderRadius: '8px',
              border: selectedTemplate === 'legal_notice' ? '2px solid #d97706' : '1px solid #cbd5e1',
              backgroundColor: selectedTemplate === 'legal_notice' ? '#fef3c7' : '#ffffff',
              color: selectedTemplate === 'legal_notice' ? '#92400e' : '#64748b',
              fontWeight: '800',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: selectedTemplate === 'legal_notice' ? '0 2px 6px rgba(217,119,6,0.2)' : 'none',
              transition: 'all 0.2s ease'
            }}
          >
            💼 Formal Legal Notice & Demand Engine
            <span style={{ 
              backgroundColor: '#d97706', 
              color: 'white', 
              fontSize: '0.68rem', 
              padding: '0.15rem 0.45rem', 
              borderRadius: '10px', 
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Out-of-Court Notice
            </span>
          </button>
        </div>

        {/* ACTIVE MODE BADGE INDICATOR */}
        {selectedTemplate === 'legal_notice' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', backgroundColor: '#fffbebe6', border: '1px solid #fcd34d', padding: '0.35rem 0.75rem', borderRadius: '20px', color: '#b45309', fontSize: '0.78rem', fontWeight: '800' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#d97706', display: 'inline-block' }}></span>
            Active Mode: Out-of-Court Legal Notice (No Court Header / Prayer Required)
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', padding: '0.35rem 0.75rem', borderRadius: '20px', color: '#0369a1', fontSize: '0.78rem', fontWeight: '800' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#0284c7', display: 'inline-block' }}></span>
            Active Mode: High Court & Supreme Court Pleading Engine
          </div>
        )}
      </div>

      {/* SUPERUSER / DATABASE ADMINISTRATOR MASTER ACCESS CONTROL PANEL */}
      {isDBA && (
        <div style={{ backgroundColor: '#0f172a', color: 'white', borderRadius: '14px', padding: '1.25rem 1.5rem', marginBottom: '1.5rem', boxShadow: '0 8px 20px rgba(15,23,42,0.3)', border: '2px solid #38bdf8' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Crown size={22} color="#ecc94b" /> Superuser & Database Administrator Master Access Control Panel
              </h3>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#94a3b8' }}>
                Master override options to modify subscription requirements, grant free trial periods, or exempt users.
              </p>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              
              {/* GLOBAL FREE ACCESS TOGGLE */}
              <button
                type="button"
                onClick={() => setGlobalFreeAccess(!globalFreeAccess)}
                style={{
                  padding: '0.55rem 1.1rem',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: globalFreeAccess ? '#16a34a' : '#dc2626',
                  color: 'white',
                  fontWeight: '800',
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                <Zap size={16} /> {globalFreeAccess ? 'Global Free Access: ENABLED (All Users Free)' : 'Global Subscription Check: ENFORCED'}
              </button>

              {/* USER SUBSCRIPTION TOGGLE */}
              <button
                type="button"
                onClick={() => setUserSubscriptionActive(!userSubscriptionActive)}
                style={{
                  padding: '0.55rem 1.1rem',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: userSubscriptionActive ? '#0284c7' : '#475569',
                  color: 'white',
                  fontWeight: '800',
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                <UserCheck size={16} /> {userSubscriptionActive ? 'Current User: SUBSCRIBED' : 'Current User: UNPAID'}
              </button>

              {/* FREE TRIAL PERIOD DAYS SELECTOR */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', backgroundColor: '#1e293b', padding: '0.35rem 0.75rem', borderRadius: '8px', border: '1px solid #334155' }}>
                <Calendar size={16} color="#38bdf8" />
                <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#cbd5e1' }}>Free Trial:</span>
                <select
                  value={freeTrialDays}
                  onChange={(e) => setFreeTrialDays(Number(e.target.value))}
                  style={{ backgroundColor: '#0f172a', color: '#ecc94b', border: '1px solid #475569', borderRadius: '4px', padding: '0.2rem 0.4rem', fontSize: '0.8rem', fontWeight: '800' }}
                >
                  <option value={7}>7 Days Free</option>
                  <option value={30}>30 Days Free</option>
                  <option value={90}>90 Days Free</option>
                  <option value={365}>1 Year Exemption</option>
                  <option value={9999}>Permanent Free</option>
                </select>
              </div>

            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', alignItems: 'start' }}>
        
        {/* LEFT COLUMN: PLEADING FORM & MULTI-PARTY CONTROLS */}
        <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #cbd5e1', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '1.5rem' }}>
          
          <h3 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0f172a', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FileText size={20} color="#0284c7" /> 1. Select Template & Court Forum
          </h3>

          <form onSubmit={handleGenerate} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            
            {/* SEARCHABLE PLEADING TEMPLATE SELECTOR */}
            <div style={{ position: 'relative' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '800', color: '#475569', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                Pleading Template ({pleadingOptions.length} Options)
              </label>

              {/* Selected Value Box Trigger */}
              <div 
                onClick={() => setIsTemplateDropdownOpen(!isTemplateDropdownOpen)}
                style={{
                  width: '100%',
                  minHeight: '44px',
                  padding: '0.6rem 0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.88rem',
                  backgroundColor: '#f8fafc',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  boxSizing: 'border-box'
                }}
              >
                <span>
                  {pleadingOptions.find(t => t.id === selectedTemplate)?.name || selectedTemplate}
                </span>
                <span style={{ fontSize: '0.75rem', color: '#0284c7', fontWeight: 'bold' }}>{isTemplateDropdownOpen ? '▲' : '▼'}</span>
              </div>

              {/* Searchable Options Overlay */}
              {isTemplateDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: 'white',
                  border: '2px solid #0284c7',
                  borderRadius: '10px',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                  maxHeight: '280px',
                  overflowY: 'auto',
                  zIndex: 9999,
                  padding: '0.5rem',
                  boxSizing: 'border-box'
                }}>
                  <input
                    type="text"
                    autoFocus
                    value={templateSearchQuery}
                    onChange={(e) => setTemplateSearchQuery(e.target.value)}
                    placeholder="🔍 Search template e.g. writ, bail, civil, service..."
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '6px',
                      border: '1px solid #94a3b8',
                      fontSize: '0.85rem',
                      marginBottom: '0.5rem',
                      boxSizing: 'border-box',
                      outline: 'none'
                    }}
                  />
                  {pleadingOptions
                    .filter(t => t.name.toLowerCase().includes(templateSearchQuery.toLowerCase()) || (t.desc && t.desc.toLowerCase().includes(templateSearchQuery.toLowerCase())))
                    .map(t => (
                      <div
                        key={t.id}
                        onClick={() => {
                          setSelectedTemplate(t.id);
                          setIsTemplateDropdownOpen(false);
                          setTemplateSearchQuery('');
                        }}
                        style={{
                          padding: '0.55rem 0.75rem',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: selectedTemplate === t.id ? '800' : '600',
                          backgroundColor: selectedTemplate === t.id ? '#e0f2fe' : 'transparent',
                          color: selectedTemplate === t.id ? '#0369a1' : '#1e293b',
                          marginBottom: '2px',
                          transition: 'background 0.15s ease'
                        }}
                        onMouseEnter={(e) => { if (selectedTemplate !== t.id) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                        onMouseLeave={(e) => { if (selectedTemplate !== t.id) e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        {t.name}
                      </div>
                    ))}
                  {pleadingOptions.filter(t => t.name.toLowerCase().includes(templateSearchQuery.toLowerCase()) || (t.desc && t.desc.toLowerCase().includes(templateSearchQuery.toLowerCase()))).length === 0 && (
                    <div style={{ padding: '0.5rem', fontSize: '0.82rem', color: '#64748b', textAlign: 'center' }}>
                      No matching template found
                    </div>
                  )}
                </div>
              )}

              {selectedTemplate === 'others' && (
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <input
                    type="text"
                    value={customTemplateInput}
                    onChange={(e) => setCustomTemplateInput(e.target.value)}
                    placeholder="Enter Custom Pleading Name e.g. Election Petition / Rent Suit"
                    style={{ flex: 1, height: '42px', padding: '0 0.75rem', borderRadius: '8px', border: '2px solid #7c3aed', fontSize: '0.85rem' }}
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomTemplate}
                    style={{ padding: '0 1rem', borderRadius: '8px', backgroundColor: '#7c3aed', color: 'white', border: 'none', fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer' }}
                  >
                    Add Template
                  </button>
                </div>
              )}
            </div>

            {/* SEARCHABLE FORUM / COURT NAME SELECTOR (HIDDEN IN LEGAL NOTICE MODE) */}
            {selectedTemplate === 'legal_notice' ? (
              <div style={{ backgroundColor: '#fffbebe6', border: '1px solid #fcd34d', borderRadius: '10px', padding: '0.85rem 1rem', color: '#b45309', fontSize: '0.84rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ fontSize: '1.2rem' }}>💼</span>
                <div>
                  <div style={{ fontWeight: '800', color: '#92400e' }}>OUT-OF-COURT FORMAL LEGAL NOTICE MODE ACTIVE</div>
                  <div style={{ fontSize: '0.78rem', color: '#b45309', fontWeight: '600', marginTop: '2px' }}>
                    No Judicial Court Name is required. Legal Notice will be dispatched directly to Defaulters / Recipients.
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '800', color: '#0369a1', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                  🏛️ Forum / Court Header Name (Dropdown List)
                </label>

                {/* Selected Value Box Trigger */}
                <div 
                  onClick={() => setIsCourtDropdownOpen(!isCourtDropdownOpen)}
                  style={{
                    width: '100%',
                    minHeight: '44px',
                    padding: '0.6rem 0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.88rem',
                    backgroundColor: 'white',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxSizing: 'border-box'
                  }}
                >
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '90%' }}>
                    {selectedCourt}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#0284c7', fontWeight: 'bold' }}>{isCourtDropdownOpen ? '▲' : '▼'}</span>
                </div>

                {/* Searchable Options Overlay */}
                {isCourtDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: 'white',
                    border: '2px solid #0284c7',
                    borderRadius: '10px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                    maxHeight: '280px',
                    overflowY: 'auto',
                    zIndex: 9999,
                    padding: '0.5rem',
                    boxSizing: 'border-box'
                  }}>
                    <input
                      type="text"
                      autoFocus
                      value={courtSearchQuery}
                      onChange={(e) => setCourtSearchQuery(e.target.value)}
                      placeholder="🔍 Search court e.g. Lahore, Karachi, Supreme, Quetta, High Court..."
                      style={{
                        width: '100%',
                        padding: '0.5rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #94a3b8',
                        fontSize: '0.85rem',
                        marginBottom: '0.5rem',
                        boxSizing: 'border-box',
                        outline: 'none'
                      }}
                    />
                    {courtOptions
                      .filter(c => c.toLowerCase().includes(courtSearchQuery.toLowerCase()))
                      .map((c, i) => (
                        <div
                          key={i}
                          onClick={() => {
                            setSelectedCourt(c);
                            setIsCourtDropdownOpen(false);
                            setCourtSearchQuery('');
                          }}
                          style={{
                            padding: '0.55rem 0.75rem',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: selectedCourt === c ? '800' : '600',
                            backgroundColor: selectedCourt === c ? '#e0f2fe' : 'transparent',
                            color: selectedCourt === c ? '#0369a1' : '#1e293b',
                            marginBottom: '2px',
                            transition: 'background 0.15s ease'
                          }}
                          onMouseEnter={(e) => { if (selectedCourt !== c) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                          onMouseLeave={(e) => { if (selectedCourt !== c) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          {c}
                        </div>
                      ))}
                    {courtOptions.filter(c => c.toLowerCase().includes(courtSearchQuery.toLowerCase())).length === 0 && (
                      <div style={{ padding: '0.5rem', fontSize: '0.82rem', color: '#64748b', textAlign: 'center' }}>
                        No matching court found
                      </div>
                    )}
                  </div>
                )}

                {selectedCourt === 'Others (Specify New Court Name)' && (
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <input
                      type="text"
                      value={customCourtInput}
                      onChange={(e) => setCustomCourtInput(e.target.value)}
                      placeholder="Enter New Court Name e.g. IN THE COURT OF ELECTION TRIBUNAL, QUETTA"
                      style={{ flex: 1, height: '42px', padding: '0 0.75rem', borderRadius: '8px', border: '2px solid #0284c7', fontSize: '0.85rem' }}
                    />
                    <button
                      type="button"
                      onClick={handleAddCustomCourt}
                      style={{ padding: '0 1rem', borderRadius: '8px', backgroundColor: '#0284c7', color: 'white', border: 'none', fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer' }}
                    >
                      Add to List
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* DYNAMIC MULTIPLE PETITIONERS / CLIENTS (FROM) */}
            <div style={{ backgroundColor: selectedTemplate === 'legal_notice' ? '#fffbebe6' : '#f0f9ff', padding: '1rem', borderRadius: '10px', border: selectedTemplate === 'legal_notice' ? '1px solid #fde68a' : '1px solid #bae6fd' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem' }}>
                <label style={{ fontSize: '0.82rem', fontWeight: '800', color: selectedTemplate === 'legal_notice' ? '#b45309' : '#0369a1', textTransform: 'uppercase', display: 'flex', alignItems: 'center' }}>
                  👤 {selectedTemplate === 'legal_notice' ? 'Notice Issuers / Aggrieved Clients (From)' : 'Petitioners / Plaintiffs'} ({petitioners.length}) <span style={{ color: '#dc2626', fontSize: '1.1rem', marginLeft: '4px', fontWeight: 'bold' }}>*</span>
                </label>
                <button
                  type="button"
                  onClick={handleAddPetitioner}
                  style={{ padding: '0.3rem 0.65rem', borderRadius: '6px', backgroundColor: selectedTemplate === 'legal_notice' ? '#d97706' : '#0284c7', color: 'white', border: 'none', fontSize: '0.75rem', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                >
                  <Plus size={14} /> Add {selectedTemplate === 'legal_notice' ? 'Client' : 'Petitioner'}
                </button>
              </div>

              {petitioners.map((pet, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input
                    type="text"
                    required
                    value={pet}
                    onChange={(e) => handlePetitionerChange(idx, e.target.value)}
                    placeholder={selectedTemplate === 'legal_notice' ? `Client / Notice Issuer No. ${idx + 1} Name, Address & Contact *` : `Petitioner No. ${idx + 1} Name, Address & CNIC *`}
                    style={{ flex: 1, height: '40px', padding: '0 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem', boxSizing: 'border-box' }}
                  />
                  {petitioners.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemovePetitioner(idx)}
                      style={{ padding: '0 0.6rem', borderRadius: '6px', border: 'none', backgroundColor: '#fee2e2', color: '#dc2626', cursor: 'pointer' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* DYNAMIC MULTIPLE RESPONDENTS / RECIPIENTS (TO) */}
            <div style={{ backgroundColor: '#fff1f2', padding: '1rem', borderRadius: '10px', border: '1px solid #fecdd3' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem' }}>
                <label style={{ fontSize: '0.82rem', fontWeight: '800', color: '#9f1239', textTransform: 'uppercase', display: 'flex', alignItems: 'center' }}>
                  🏢 {selectedTemplate === 'legal_notice' ? 'Notice Recipients / Defaulters (To)' : 'Respondents / Defendants'} ({respondents.length}) <span style={{ color: '#dc2626', fontSize: '1.1rem', marginLeft: '4px', fontWeight: 'bold' }}>*</span>
                </label>
                <button
                  type="button"
                  onClick={handleAddRespondent}
                  style={{ padding: '0.3rem 0.65rem', borderRadius: '6px', backgroundColor: '#dc2626', color: 'white', border: 'none', fontSize: '0.75rem', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                >
                  <Plus size={14} /> Add {selectedTemplate === 'legal_notice' ? 'Recipient' : 'Respondent'}
                </button>
              </div>

              {respondents.map((resp, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input
                    type="text"
                    required
                    value={resp}
                    onChange={(e) => handleRespondentChange(idx, e.target.value)}
                    placeholder={selectedTemplate === 'legal_notice' ? `Recipient / Defaulter No. ${idx + 1} Name, Department & Address *` : `Respondent No. ${idx + 1} Department / Name & Address *`}
                    style={{ flex: 1, height: '40px', padding: '0 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem', boxSizing: 'border-box' }}
                  />
                  {respondents.length > 1 && (
                    <button
                    onClick={() => handleRemoveRespondent(idx)}
                      style={{ padding: '0 0.6rem', borderRadius: '6px', border: 'none', backgroundColor: '#fee2e2', color: '#dc2626', cursor: 'pointer' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* PRIMARY INPUT: Statement of Facts WITH MANDATORY RED ASTERISK & AI Deep Knowledge Augmentation Box */}
            <div style={{ backgroundColor: selectedTemplate === 'legal_notice' ? '#fffbebe6' : '#f0f9ff', padding: '1.1rem', borderRadius: '12px', border: selectedTemplate === 'legal_notice' ? '2px solid #f59e0b' : '2px solid #0284c7', boxShadow: '0 3px 10px rgba(2,132,199,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.4rem' }}>
                <label style={{ fontSize: '0.84rem', fontWeight: '800', color: selectedTemplate === 'legal_notice' ? '#92400e' : '#0369a1', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  📝 {selectedTemplate === 'legal_notice' ? 'Brief Facts of Grievance & Breach (Primary Input)' : 'Statement of Facts (Provide Only Brief Facts)'} <span style={{ color: '#dc2626', fontSize: '1.2rem', marginLeft: '2px', fontWeight: 'bold' }}>*</span>
                </label>
                <span style={{ fontSize: '0.72rem', backgroundColor: selectedTemplate === 'legal_notice' ? '#d97706' : '#0284c7', color: 'white', fontWeight: '800', padding: '0.25rem 0.6rem', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                  <Sparkles size={12} /> AI Fact Refinement & Pleading Hints Active
                </span>
              </div>
              <textarea
                rows={6}
                required
                value={keyFacts}
                onChange={(e) => setKeyFacts(e.target.value)}
                placeholder={selectedTemplate === 'legal_notice' ? "Enter brief facts of grievance or breach of contract here... Elum Legal AI will automatically use its deep legal knowledge, Law Directory database, Law Dictionary & statutory laws to refine your facts into an authentic 8-10 paragraph Legal Notice of Demand, 14-day compliance term, litigation warning, and pre-litigation hints." : "Enter only the brief facts of your case here... The wording does not need to be formal — Elum Legal AI will automatically elevate and refine your facts into pristine High Court / Supreme Court pleading prose (6-10 paragraphs), extract the cause of action, concerned sections, Law Directory precedents & Law Dictionary maxims, standardized prayer clause, and generate 'Pleading Hints for Practitioners' on a separate page."}
                style={{ width: '100%', padding: '0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box', backgroundColor: '#ffffff', lineHeight: '1.5' }}
              />
              <p style={{ margin: '0.45rem 0 0 0', fontSize: '0.76rem', color: selectedTemplate === 'legal_notice' ? '#b45309' : '#0369a1', fontWeight: '600' }}>
                💡 <strong>AI Legal Synthesizer:</strong> {selectedTemplate === 'legal_notice' ? 'Automatically weaves statutory breach provisions, contract act sections, legal maxims, and pre-litigation hints for counsel.' : 'Automatically weaves Constitutional Articles (4, 9, 10-A, 25), legal maxims (Audi alteram partem, Locus poenitentiae, Functus officio), statutory sections, Law Directory precedents (e.g. PLD 2017 Lah 1, 2023 SCMR 456, PLD 2025 SC 101), and generates Pleading Hints for Practitioners on a separate page.'}
              </p>
            </div>

            {/* COLLAPSIBLE ADVANCED LEGAL OPTIONS (OPTIONAL - AI EXTRACTS AUTOMATICALLY IF LEFT BLANK) */}
            <div style={{
              border: '1px solid #cbd5e1',
              borderRadius: '10px',
              overflow: 'hidden',
              backgroundColor: '#f8fafc'
            }}>
              <div 
                onClick={() => setIsAdvancedOptionsOpen(!isAdvancedOptionsOpen)}
                style={{
                  padding: '0.75rem 1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  backgroundColor: isAdvancedOptionsOpen ? '#f1f5f9' : '#f8fafc',
                  borderBottom: isAdvancedOptionsOpen ? '1px solid #cbd5e1' : 'none',
                  fontWeight: '700',
                  fontSize: '0.84rem',
                  color: '#334155',
                  transition: 'background 0.15s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                  <span>⚙️ Advanced Legal Options</span>
                  <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: '500' }}>
                    (Optional — AI automatically extracts these from Brief Facts if left blank)
                  </span>
                </div>
                <span style={{ fontSize: '0.78rem', color: '#0284c7', fontWeight: '800' }}>
                  {isAdvancedOptionsOpen ? '▲ Hide Options' : '▼ Show Options'}
                </span>
              </div>

              {isAdvancedOptionsOpen && (
                <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem', backgroundColor: '#ffffff' }}>
                  {/* PRE-FILLED CAUSE OF ACTION FIELD (HIDDEN IN LEGAL NOTICE MODE) */}
                  {selectedTemplate !== 'legal_notice' && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <label style={{ fontSize: '0.8rem', fontWeight: '800', color: '#334155', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          📅 Cause of Action & Accrual Date (Optional)
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <button
                            type="button"
                            onClick={() => {
                              const key = defaultCauseOfActionMap[selectedTemplate] ? selectedTemplate : 'others';
                              setCauseOfAction(defaultCauseOfActionMap[key] || defaultCauseOfActionMap['others']);
                            }}
                            style={{ fontSize: '0.72rem', backgroundColor: '#e0f2fe', color: '#0369a1', border: '1px solid #7dd3fc', borderRadius: '4px', padding: '0.2rem 0.5rem', fontWeight: '800', cursor: 'pointer' }}
                          >
                            💡 Suggest Default
                          </button>
                        </div>
                      </div>
                      <textarea
                        rows={3}
                        value={causeOfAction}
                        onChange={(e) => setCauseOfAction(e.target.value)}
                        placeholder="Leave blank for AI auto-extraction, or enter specific accrual date..."
                        style={{ width: '100%', padding: '0.65rem 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem', boxSizing: 'border-box', backgroundColor: '#f8fafc' }}
                      />
                    </div>
                  )}

                  {/* PRE-FILLED STATUTORY PROVISIONS & RELEVANT SECTIONS */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <label style={{ fontSize: '0.8rem', fontWeight: '800', color: '#334155', textTransform: 'uppercase' }}>
                        📜 Statutory Provisions & Relevant Sections (Optional)
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          const key = defaultStatutoryMap[selectedTemplate] ? selectedTemplate : 'others';
                          setStatutorySections(defaultStatutoryMap[key] || defaultStatutoryMap['others']);
                        }}
                        style={{ fontSize: '0.72rem', backgroundColor: '#e0f2fe', color: '#0369a1', border: '1px solid #7dd3fc', borderRadius: '4px', padding: '0.2rem 0.5rem', fontWeight: '800', cursor: 'pointer' }}
                      >
                        💡 Suggest Default
                      </button>
                    </div>
                    <input
                      type="text"
                      value={statutorySections}
                      onChange={(e) => setStatutorySections(e.target.value)}
                      placeholder="Leave blank for AI auto-extraction, or enter specific sections..."
                      style={{ width: '100%', height: '42px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* PRE-FILLED PRAYER / RELIEF CLAUSE FIELD (HIDDEN IN LEGAL NOTICE MODE) */}
                  {selectedTemplate !== 'legal_notice' && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <label style={{ fontSize: '0.8rem', fontWeight: '800', color: '#334155', textTransform: 'uppercase' }}>
                          🎯 Prayer / Relief Clause (Optional)
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <button
                            type="button"
                            onClick={() => {
                              const key = defaultPrayerClauseMap[selectedTemplate] ? selectedTemplate : 'others';
                              setReliefRequested(defaultPrayerClauseMap[key] || defaultPrayerClauseMap['others']);
                            }}
                            style={{ fontSize: '0.72rem', backgroundColor: '#dcfce7', color: '#15803d', border: '1px solid #86efac', borderRadius: '4px', padding: '0.2rem 0.5rem', fontWeight: '800', cursor: 'pointer' }}
                          >
                            💡 Suggest Default
                          </button>
                        </div>
                      </div>
                      <textarea
                        rows={4}
                        value={reliefRequested}
                        onChange={(e) => setReliefRequested(e.target.value)}
                        placeholder="Leave blank for AI auto-extraction, or enter customized relief..."
                        style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem', boxSizing: 'border-box', backgroundColor: '#f8fafc' }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={drafting}
              style={{
                width: '100%',
                padding: '0.95rem 1.5rem',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: selectedTemplate === 'legal_notice' ? '#d97706' : '#0284c7',
                color: 'white',
                cursor: drafting ? 'not-allowed' : 'pointer',
                fontWeight: '800',
                fontSize: '0.98rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.6rem',
                boxShadow: selectedTemplate === 'legal_notice' ? '0 4px 14px rgba(217,119,6,0.4)' : '0 4px 14px rgba(2,132,199,0.4)',
                marginTop: '0.5rem'
              }}
            >
              <Sparkles size={20} /> {drafting ? 'Compiling & Augmenting Deep Legal Draft...' : selectedTemplate === 'legal_notice' ? '💼 Generate AI Deep Augmented Legal Notice & Pre-Litigation Hints' : '⚡ Generate AI Deep Augmented Legal Pleading Draft & Pleading Hints'}
            </button>

          </form>

        </div>

        {/* RIGHT COLUMN: FORMAL COURT PLEADING & MS WORD (.DOC ONLY) EXPORT */}
        <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #cbd5e1', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: '800', color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <BookOpen size={20} color="#0284c7" /> 2. Court Pleading Preview (Courier New 12pt, 1.5 Spacing, Bold/Underlined Subject)
            </h3>

            {generatedDraft && (
              <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap' }}>
                <button
                  onClick={handleScrollToHints}
                  title="Jump to Pleading Hints for Practitioners at the bottom of the draft"
                  style={{ padding: '0.45rem 0.85rem', borderRadius: '6px', border: '1px solid #d97706', backgroundColor: '#fffbeb', color: '#b45309', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '0.35rem', boxShadow: '0 1px 3px rgba(217,119,6,0.15)' }}
                >
                  <Sparkles size={14} color="#d97706" /> Pleading Hints
                </button>

                <button
                  onClick={handleCopyHints}
                  title="Copy only Pleading Hints for Practitioners to clipboard"
                  style={{ padding: '0.45rem 0.85rem', borderRadius: '6px', border: '1px solid #f59e0b', backgroundColor: '#fef3c7', color: '#92400e', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                >
                  {copiedHints ? <Check size={14} color="#16a34a" /> : <Copy size={14} />} {copiedHints ? 'Hints Copied!' : 'Copy Hints'}
                </button>

                <button
                  onClick={handleCopy}
                  title="Copy full draft text to clipboard"
                  style={{ padding: '0.45rem 0.85rem', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', color: '#0f172a', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                >
                  {copied ? <Check size={14} color="#16a34a" /> : <Copy size={14} />} {copied ? 'Copied!' : 'Copy Draft'}
                </button>

                <button
                  onClick={handlePrint}
                  style={{ padding: '0.45rem 0.85rem', borderRadius: '6px', border: 'none', backgroundColor: '#0f172a', color: '#ecc94b', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                >
                  <Printer size={14} /> Print
                </button>

                {/* EXPLICIT MS WORD (.DOC ONLY) DOWNLOAD BUTTON - RESTRICTED TO SUBSCRIBED / EXEMPTED USERS */}
                <button
                  onClick={handleDownloadDoc}
                  title="Download in Microsoft Word (.doc) format with Courier New 12pt, 1.5 Spacing & Bold/Underlined Subject"
                  style={{ padding: '0.45rem 0.95rem', borderRadius: '6px', border: 'none', backgroundColor: '#16a34a', color: 'white', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '0.35rem', boxShadow: '0 2px 4px rgba(22,163,74,0.3)' }}
                >
                  <Download size={14} /> Download MS Word (.doc)
                </button>
              </div>
            )}
          </div>

          {/* COURIER NEW 12pt, JUSTIFIED, 1.5 LINE SPACING PREVIEW BOX */}
          <div style={{ 
            flex: 1, 
            minHeight: '520px', 
            maxHeight: '780px', 
            overflowY: 'auto', 
            backgroundColor: '#ffffff', 
            borderRadius: '10px', 
            border: '1px solid #cbd5e1', 
            padding: '2rem', 
            fontFamily: "'Courier New', Courier, monospace", 
            fontSize: '12pt', 
            lineHeight: '1.5', 
            textAlign: 'justify', 
            color: '#0f172a', 
            whiteSpace: 'pre-wrap',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
          }}>
            {drafting ? (
              <div style={{ textAlign: 'center', padding: '6rem 0', color: '#0284c7', fontFamily: "'Inter', sans-serif" }}>
                <Sparkles size={40} style={{ animation: 'spin 2s linear infinite', marginBottom: '1rem' }} />
                <div style={{ fontSize: '1.1rem', fontWeight: '800' }}>AI Senior Counsel is compiling legal pleading...</div>
                <p style={{ fontSize: '0.85rem', color: '#64748b' }}>Expanding facts, weaving Supreme Court citations & maxims, auto-formatting bold/underlined Court Title & Subject line.</p>
              </div>
            ) : generatedDraft ? (
              renderFormattedDraft(generatedDraft)
            ) : (
              <div style={{ textAlign: 'center', padding: '8rem 0', color: '#94a3b8', fontFamily: "'Inter', sans-serif" }}>
                <Scale size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#475569' }}>No Draft Generated Yet</div>
                <p style={{ fontSize: '0.85rem', color: '#94a3b8', maxWidth: '400px', margin: '0.5rem auto' }}>
                  Review pre-filled Cause of Action & Prayer Clause on the left pane and click <strong>"Generate AI Legal Pleading Draft"</strong>.
                </p>
              </div>
            )}
          </div>

        </div>

      </div>

    </div>
  );

  if (isModalView) {
    return (
      <div style={{
        position: 'fixed',
        top: windowState === 'fullscreen' ? 0 : 'auto',
        left: windowState === 'fullscreen' ? 0 : 'auto',
        right: windowState === 'fullscreen' ? 0 : '24px',
        bottom: windowState === 'fullscreen' ? 0 : '24px',
        width: windowState === 'fullscreen' ? '100vw' : `${windowSize.width}px`,
        height: windowState === 'fullscreen' ? '100vh' : `${windowSize.height}px`,
        maxWidth: windowState === 'fullscreen' ? '100vw' : '96vw',
        maxHeight: windowState === 'fullscreen' ? '100vh' : '94vh',
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(10px)',
        zIndex: 2500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: windowState === 'fullscreen' ? 0 : '0.5rem',
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}>
        <div style={{
          backgroundColor: colors.cardBg,
          color: colors.textColor,
          border: `2px solid ${colors.goldPrimary}`,
          borderRadius: windowState === 'fullscreen' ? '0' : '16px',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px rgba(0,0,0,0.8), 0 0 30px rgba(217, 119, 6, 0.25)',
          overflow: 'hidden',
          boxSizing: 'border-box',
          position: 'relative'
        }}>
          {content}

          {/* CLICK & DRAG 2D RESIZE HANDLE (HORIZONTAL & VERTICAL) AT BOTTOM-RIGHT CORNER */}
          {windowState !== 'fullscreen' && (
            <div 
              onMouseDown={handleStartResize}
              title="Click and drag horizontally or vertically to resize window size"
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '20px',
                height: '20px',
                cursor: 'nwse-resize',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#d97706',
                color: '#0f172a',
                borderRadius: '4px 0 14px 0',
                userSelect: 'none'
              }}
            >
              <span style={{ fontSize: '11px', fontWeight: 'bold', lineHeight: 1 }}>↘</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return content;
};

export default SmartLegalDrafting;
